import React, { useMemo, useState, useEffect } from "react";
import {
  suggestSimilarSchools,
  createGroupFromSelection,
  mergeGroups,
} from "../../lib/schoolAliases";

function useMobile(bp = 540) {
  const [m, setM] = useState(
    () => typeof window !== "undefined" && window.matchMedia(`(max-width:${bp}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${bp}px)`);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [bp]);
  return m;
}

export default function AnalyticsControlPanel({
  records,
  aliasGroups,
  hiddenSchools = [],
  onSaveGroups,
  onSaveHiddenSchools,
  onClose,
  saving,
}) {
  const isMobile = useMobile();

  const suggestions = useMemo(
    () => suggestSimilarSchools(records, aliasGroups),
    [records, aliasGroups]
  );

  const [selectedBySuggestion, setSelectedBySuggestion] = useState({});
  const [canonicalBySuggestion, setCanonicalBySuggestion] = useState({});
  const [manualKeys, setManualKeys] = useState([]);
  const [customCanonical, setCustomCanonical] = useState("");

  // Edit mode for active merge groups
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editName, setEditName] = useState("");

  // All distinct school entries from records (post-alias: canonical names for merged)
  const allEntries = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      const key = r.schoolKey ?? (r.schoolName ?? "").trim().toLowerCase();
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: r.schoolDisplay ?? r.schoolName ?? key,
          count: 0,
        });
      }
      map.get(key).count += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [records]);

  // Map from canonical key → group (for merged entries)
  const groupByKey = useMemo(() => {
    const m = {};
    for (const g of aliasGroups) {
      const canonKey = g.canonicalName.trim().toLowerCase();
      m[canonKey] = g;
      for (const k of g.memberKeys || []) m[k] = g;
    }
    return m;
  }, [aliasGroups]);

  const hiddenKeySet = useMemo(
    () => new Set(hiddenSchools.map((s) => s.key)),
    [hiddenSchools]
  );

  const hideSchool = (entry) => {
    if (hiddenKeySet.has(entry.key)) return;
    onSaveHiddenSchools([
      ...hiddenSchools,
      { key: entry.key, label: entry.label },
    ]);
  };

  const unhideSchool = (key) => {
    onSaveHiddenSchools(hiddenSchools.filter((s) => s.key !== key));
  };

  const visibleEntries = allEntries.filter((e) => !hiddenKeySet.has(e.key));
  const hiddenEntries = allEntries.filter((e) => hiddenKeySet.has(e.key));

  const toggleSuggestionSchool = (suggestionId, entry, checked) => {
    setSelectedBySuggestion((prev) => {
      const set = new Set(prev[suggestionId] || []);
      if (checked) set.add(entry.key);
      else set.delete(entry.key);
      return { ...prev, [suggestionId]: [...set] };
    });
  };

  const mergeSuggestion = (suggestion) => {
    const selectedKeys =
      selectedBySuggestion[suggestion.id] || suggestion.schools.map((s) => s.key);
    const selected = suggestion.schools.filter((s) => selectedKeys.includes(s.key));
    if (selected.length < 2) {
      alert("Select at least two schools to merge.");
      return;
    }
    const name =
      canonicalBySuggestion[suggestion.id]?.trim() || suggestion.suggestedName;
    const group = createGroupFromSelection(name, selected);
    onSaveGroups(mergeGroups(aliasGroups, group));
    setSelectedBySuggestion((prev) => {
      const next = { ...prev };
      delete next[suggestion.id];
      return next;
    });
  };

  const mergeCustom = () => {
    const selected = allEntries.filter((e) => manualKeys.includes(e.key));
    if (selected.length < 2 || !customCanonical.trim()) {
      alert("Select at least 2 schools and enter a display name.");
      return;
    }
    const group = createGroupFromSelection(customCanonical, selected);
    onSaveGroups(mergeGroups(aliasGroups, group));
    setManualKeys([]);
    setCustomCanonical("");
  };

  const removeGroup = (groupId) => {
    if (!window.confirm("Unmerge this group? Firestore records already renamed will keep their current name.")) return;
    onSaveGroups(aliasGroups.filter((g) => g.id !== groupId));
  };

  const saveGroupRename = (groupId) => {
    if (!editName.trim()) return;
    const updated = aliasGroups.map((g) =>
      g.id === groupId ? { ...g, canonicalName: editName.trim() } : g
    );
    onSaveGroups(updated);
    setEditingGroupId(null);
    setEditName("");
  };

  return (
    <div className="analytics-control-overlay" onClick={onClose}>
      <div
        className="analytics-control-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="analytics-control-header">
          <h2>Analytics Control</h2>
          <button type="button" className="control-close-btn" onClick={onClose}>
            ×
          </button>
        </header>

        <p className="control-intro">
          Merge duplicate schools or hide schools from all admin analytics and
          responses. Hidden data stays in Firestore — nothing is deleted.
        </p>

        {/* ── Hidden schools ─────────────────────────────────── */}
        <section className="control-section">
          <h3>Hidden schools ({hiddenSchools.length})</h3>
          <p className="control-intro" style={{ marginBottom: 8 }}>
            Hidden schools are excluded from every chart, leaderboard, percentage,
            competition response list, and CSV export across the admin panel.
          </p>
          {hiddenSchools.length === 0 ? (
            <p className="analytics-empty">No schools hidden.</p>
          ) : (
            <ul className="hidden-schools-list">
              {(hiddenEntries.length > 0 ? hiddenEntries : hiddenSchools).map(
                (entry) => {
                  const e =
                    typeof entry === "object" && "count" in entry
                      ? entry
                      : {
                          key: entry.key,
                          label: entry.label,
                          count: allEntries.find((x) => x.key === entry.key)
                            ?.count ?? 0,
                        };
                  return (
                    <li key={e.key} className="hidden-school-item">
                      <div>
                        <strong>{e.label}</strong>
                        <span className="suggestion-count">
                          ({e.count} registration{e.count !== 1 ? "s" : ""}{" "}
                          hidden)
                        </span>
                      </div>
                      <button
                        type="button"
                        className="control-unhide-btn"
                        disabled={saving}
                        onClick={() => unhideSchool(e.key)}
                      >
                        Show
                      </button>
                    </li>
                  );
                }
              )}
            </ul>
          )}
          {visibleEntries.length > 0 && (
            <>
              <label className="filter-label" style={{ marginTop: 12 }}>
                Hide a school
              </label>
              <div className="manual-school-grid">
                {visibleEntries.map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    className="hide-school-chip"
                    disabled={saving}
                    onClick={() => hideSchool(e)}
                    title={`Hide ${e.label} from admin panel`}
                  >
                    <span className="chip-label">{e.label}</span>
                    <span className="suggestion-count">({e.count})</span>
                    <span className="hide-chip-action">Hide</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        {/* ── Suggested duplicates ────────────────────────────── */}
        <section className="control-section">
          <h3>Suggested similar names</h3>
          {suggestions.length === 0 ? (
            <p className="analytics-empty">No new suggestions right now.</p>
          ) : (
            suggestions.map((sug) => (
              <div key={sug.id} className="suggestion-card">
                <p className="suggestion-hint">
                  These names look similar — select any to merge:
                </p>
                <ul className="suggestion-list">
                  {sug.schools.map((s) => (
                    <li key={s.key}>
                      <label>
                        <input
                          type="checkbox"
                          checked={(
                            selectedBySuggestion[sug.id] ||
                            sug.schools.map((x) => x.key)
                          ).includes(s.key)}
                          onChange={(e) =>
                            toggleSuggestionSchool(sug.id, s, e.target.checked)
                          }
                        />
                        <span>{s.label}</span>
                        <span className="suggestion-count">({s.count})</span>
                      </label>
                    </li>
                  ))}
                </ul>
                <label className="filter-label">Display as</label>
                <input
                  className="control-input"
                  value={canonicalBySuggestion[sug.id] ?? sug.suggestedName}
                  onChange={(e) =>
                    setCanonicalBySuggestion((prev) => ({
                      ...prev,
                      [sug.id]: e.target.value,
                    }))
                  }
                />
                <button
                  type="button"
                  className="export-btn"
                  disabled={saving}
                  onClick={() => mergeSuggestion(sug)}
                >
                  Merge selected
                </button>
              </div>
            ))
          )}
        </section>

        {/* ── Merge any school (ALL schools shown) ────────────── */}
        <section className="control-section">
          <h3>Merge any schools</h3>
          <p className="control-intro" style={{ marginBottom: 8 }}>
            All schools shown — <span className="merged-inline-label">highlighted</span>{" "}
            ones are already in a merge group. Selecting them will add them to a
            new group.
          </p>
          <div className="manual-school-grid">
            {allEntries.map((e) => {
              const existingGroup = groupByKey[e.key];
              return (
                <label
                  key={e.key}
                  className={`manual-school-chip ${existingGroup ? "is-merged" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={manualKeys.includes(e.key)}
                    onChange={(ev) => {
                      if (ev.target.checked) {
                        setManualKeys((k) => [...k, e.key]);
                      } else {
                        setManualKeys((k) => k.filter((x) => x !== e.key));
                      }
                    }}
                  />
                  <span className="chip-label">{e.label}</span>
                  <span className="suggestion-count">({e.count})</span>
                  {existingGroup && (
                    <span className="merged-tag">
                      → {existingGroup.canonicalName}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {allEntries.length === 0 && (
            <p className="analytics-empty">No school data found in records.</p>
          )}
          <label className="filter-label">Unified school name</label>
          <input
            className="control-input"
            placeholder="e.g. Royal College"
            value={customCanonical}
            onChange={(e) => setCustomCanonical(e.target.value)}
          />
          <button
            type="button"
            className="export-btn"
            disabled={saving || manualKeys.length < 2}
            onClick={mergeCustom}
          >
            Merge {manualKeys.length} selected
          </button>
        </section>

        {/* ── Active merges ─────────────────────────────────── */}
        <section className="control-section">
          <h3>Active merges ({aliasGroups.length})</h3>
          {aliasGroups.length === 0 ? (
            <p className="analytics-empty">No merges yet.</p>
          ) : (
            <ul className="active-merges-list">
              {aliasGroups.map((g) => (
                <li key={g.id} className="active-merge-item">
                  {editingGroupId === g.id ? (
                    <div className="merge-edit-inline">
                      <input
                        className="control-input"
                        style={{ marginBottom: 0, flex: 1 }}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="export-btn"
                        style={{ padding: "5px 12px" }}
                        disabled={saving}
                        onClick={() => saveGroupRename(g.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="control-unmerge-btn"
                        onClick={() => { setEditingGroupId(null); setEditName(""); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="merge-item-info">
                      <strong>{g.canonicalName}</strong>
                      <span className="merge-aliases">
                        ← {(g.memberLabels || g.memberKeys).join(", ")}
                      </span>
                    </div>
                  )}
                  {editingGroupId !== g.id && (
                    <div className="merge-item-actions">
                      <button
                        type="button"
                        className="control-edit-btn"
                        disabled={saving}
                        onClick={() => {
                          setEditingGroupId(g.id);
                          setEditName(g.canonicalName);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="control-unmerge-btn"
                        disabled={saving}
                        onClick={() => removeGroup(g.id)}
                      >
                        Unmerge
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {saving && (
          <p className="control-saving">
            <span className="analytics-spinner" style={{ width: 14, height: 14, borderWidth: 2, display: "inline-block", verticalAlign: "middle", marginRight: 6 }} />
            Saving…
          </p>
        )}
      </div>
    </div>
  );
}
